import { useState } from 'react';
import cn from 'classnames';
import toast, { CheckmarkIcon } from 'react-hot-toast';
import Button from '@/components/ui/button-old';
import { useCart } from '@/components/cart/lib/cart.context';
import usePrice from '@/lib/hooks/use-price';
import type { Product } from '@/types';
import { generateCartItem } from './lib/generate-cart-item';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import routes from '@/config/routes';
import AnchorLink from '../ui/links/anchor-link';
import { trackEvent } from '@/data/utils/utils';

interface Props {
  item: Product;
  status?: string;
  className?: string;
  toastClassName?: string;
  withPrice?: boolean;
  variant?: 'text' | 'outline' | 'solid' | 'icon' | 'solidDanger';
  leftIcon?: React.ReactNode;
}

export default function AddToCart({
  item,
  status = 'publish',
  className,
  toastClassName,
  withPrice = true,
  variant = 'solid',
  leftIcon,
}: Props) {
  const { t } = useTranslation('common');
  const { addItemToCart, updateCartLanguage, language, isInStock, isInCart } =
    useCart();
  const [addToCartLoader, setAddToCartLoader] = useState(false);
  const [cartingSuccess, setCartingSuccess] = useState(isInCart(item?.id));
  const { price } = usePrice({
    amount: item?.sale_price ? item?.sale_price : item?.price,
    baseAmount: item?.price,
  });
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  function handleCheckout() {
    router.push(routes.checkout, undefined, {
      locale: language,
    });
    // setLoading(true);
    // setTimeout(() => {
    //   setLoading(false);
    // }, 600);
  }
  function handleAddToCart(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    trackEvent('add_to_cart', item);

    setAddToCartLoader(true);
    // setTimeout(() => {
    setAddToCartLoader(false);
    addSuccessfully();
    // }, 650);
  }
  function addSuccessfully() {
    if (item?.language !== language) {
      updateCartLanguage(item?.language);
    }
    setCartingSuccess(true);
    addItemToCart(generateCartItem(item), 1);
    toast.success(<b>{t('text-add-to-cart-message')}</b>, {
      className: toastClassName,
    });
    // setTimeout(() => {
    setCartingSuccess(false);
    // }, 800);
  }
  return (
    <>
      {status !== 'publish' ? (
        <div className="flex items-center gap-2 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-5 w-5 text-yellow-600"
          >
            <path
              fillRule="evenodd"
              d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
              clipRule="evenodd"
            />
          </svg>
          <span>Preview only - This product is not published yet</span>
        </div>
      ) : isInCart(item?.id) ? (
        <div className="flex w-full flex-col gap-2">
          <AnchorLink href={routes.checkout}>
            <Button
              variant="outline"
              style={{
                borderColor:
                  'hsl(var(--twc-brand) / var(--twc-brand-opacity, var(--tw-bg-opacity)))',
              }}
              className={cn(
                'hover:bg-brand-dark',
                className,
                'hover:bg-brand-dark'
              )}
              // onClick={() => handleCheckout()}
              // isLoading={loading}
            >
              Go to checkout
            </Button>
          </AnchorLink>
          <div className={cn('flex')}>
            <div className="flex items-center justify-center">
              <CheckmarkIcon
                style={{
                  background:
                    'hsl(var(--twc-brand) / var(--twc-brand-opacity, var(--tw-bg-opacity)))',
                }}
                className="mx-2 h-6 w-6 text-brand"
              />
            </div>
            <p>Added to cart</p>
          </div>
        </div>
      ) : (
        <Button
          onClick={(e) => handleAddToCart(e)}
          variant={variant}
          className={cn(
            'relative',
            cartingSuccess
              ? 'is-carting pointer-events-none cursor-not-allowed'
              : 'pointer-events-auto cursor-pointer',
            className
          )}
          disabled={isInStock(item?.id) || item?.status !== 'publish'}
        >
          {leftIcon && leftIcon}
          {t('text-add-to-cart')} {withPrice && price}
          {/* <svg
            viewBox="0 0 37 37"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute top-auto bottom-auto right-3 h-auto w-5 xs:right-4 xs:w-6"
          >
            <path
              fill="none"
              stroke="currentColor"
              strokeLinejoin="round"
              strokeMiterlimit="10"
              strokeWidth="2.3"
              d="M30.5 6.5h0c6.6 6.6 6.6 17.4 0 24h0c-6.6 6.6-17.4 6.6-24 0h0c-6.6-6.6-6.6-17.4 0-24h0c6.6-6.7 17.4-6.7 24 0z"
              className="circle path"
            />
            <path
              fill="none"
              stroke="currentColor"
              strokeLinejoin="round"
              strokeMiterlimit="10"
              strokeWidth="2.3"
              d="M11.6 20L15.9 24.2 26.4 13.8"
              className="tick path"
            />
          </svg> */}
        </Button>
      )}
    </>
  );
}
